import { ScanOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Divider, Empty, List, Space, Typography } from 'antd';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useStartupSession } from '@/app/session/StartupSessionContext';
import { useAppRefreshHandler } from '@/app/shell/app-refresh';
import { DeleteLocalDraftButton } from '@/components/DeleteLocalDraftButton';
import { CaptureSessionMeterRecordRow } from '@/components/MeterRecordSummary';
import { StatsRow } from '@/components/StatsRow';
import { useLocationBootstrap } from '@/features/location-bootstrap/LocationBootstrapContext';
import { useInventoryMeterSessions } from '@/features/meters/useInventoryMeterSessions';
import { useLocationInventoryStats } from '@/features/today/useLocationInventoryStats';
import { useStorageHealth } from '@/features/today/useStorageHealth';
import {
  CAPTURE_SESSION_STATUSES,
  type CaptureSession,
  isDeletableLocalSession,
} from '@/shared/capture/capture-session';
import { messages } from './messages';

interface TodayViewProps {
  onOpenQueuedMeter: (sessionId: string) => void;
  onOpenScan: () => void;
}

export function TodayView({ onOpenQueuedMeter, onOpenScan }: TodayViewProps) {
  const intl = useIntl();
  const { activeLocation } = useStartupSession();
  const {
    error: statsError,
    isLoading: isLoadingStats,
    refresh: refreshStats,
    stats,
  } = useLocationInventoryStats(activeLocation);
  const { bootstrap, error: bootstrapError, isLoading: isLoadingBootstrap } = useLocationBootstrap();
  const {
    deleteLocalDraft,
    deletingSessionId,
    error: queuedMetersError,
    isLoading: isLoadingQueuedMeters,
    refresh: refreshQueuedMeters,
    sessions,
  } = useInventoryMeterSessions({ locationId: activeLocation?.locationId });
  const { refresh: refreshStorageHealth, storageHealth } = useStorageHealth({ locationId: activeLocation?.locationId });
  const localDrafts = useMemo(() => sessions.filter(isLocalDraft), [sessions]);
  const recentMeters = useMemo(
    () =>
      sessions
        .filter(
          session =>
            session.status === CAPTURE_SESSION_STATUSES.queued ||
            session.status === CAPTURE_SESSION_STATUSES.synced ||
            session.status === CAPTURE_SESSION_STATUSES.needsReview,
        )
        .slice(0, 5),
    [sessions],
  );

  useAppRefreshHandler(
    useCallback(
      () => Promise.all([refreshStats({ refreshCache: true }), refreshStorageHealth(), refreshQueuedMeters()]),
      [refreshStats, refreshStorageHealth, refreshQueuedMeters],
    ),
  );

  const metersTotal = bootstrap?.existingMeters.length ?? stats?.metersTotal ?? 0;
  const buildingsTotal = bootstrap?.buildings.length ?? stats?.buildingsTotal ?? 0;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Button block icon={<ScanOutlined />} onClick={onOpenScan} size="large" style={{ height: 72 }} type="primary">
        {intl.formatMessage(messages.todayStartPasport)}
      </Button>

      <Divider style={{ margin: 0 }} />

      <StatsRow
        items={[
          {
            label: intl.formatMessage(messages.todayMetersInLocation),
            value: isLoadingBootstrap && !bootstrap ? '-' : metersTotal,
          },
          {
            label: intl.formatMessage(messages.todayBuildingsInLocation),
            value: isLoadingBootstrap && !bootstrap ? '-' : buildingsTotal,
          },
          { label: intl.formatMessage(messages.todayLocalDrafts), value: storageHealth?.draftCount ?? 0 },
        ]}
      />

      {statsError ? <Alert message={statsError} showIcon type="warning" /> : null}
      {bootstrapError ? <Alert closable message={bootstrapError} showIcon type="warning" /> : null}
      {queuedMetersError ? <Alert closable message={queuedMetersError} showIcon type="warning" /> : null}
      {storageHealth?.warningLevel && storageHealth.warningLevel !== 'ok' ? (
        <Alert
          closable
          message={intl.formatMessage(messages.todayLocalPhotoStorageWarning, { level: storageHealth.warningLevel })}
          showIcon
          type="warning"
        />
      ) : null}

      {localDrafts.length > 0 ? (
        <Card size="small" title={intl.formatMessage(messages.todayLocalDrafts)}>
          <List
            dataSource={localDrafts}
            rowKey={session => session.id}
            renderItem={session => (
              <CaptureSessionMeterRecordRow
                actions={[
                  <DeleteLocalDraftButton
                    isDeleting={deletingSessionId === session.id}
                    key="delete"
                    onDelete={() => void deleteLocalDraft(session.id).then(refreshStorageHealth)}
                  />,
                ]}
                detailsFallback={intl.formatMessage(messages.metersNoPlacementDetails)}
                onOpen={() => openSession(session, onOpenQueuedMeter)}
                session={session}
              />
            )}
          />
        </Card>
      ) : null}

      <Card size="small" title={intl.formatMessage(messages.todayRecentMeters)}>
        {recentMeters.length > 0 ? (
          <List
            dataSource={recentMeters}
            loading={isLoadingQueuedMeters}
            rowKey={session => session.id}
            renderItem={session => (
              <CaptureSessionMeterRecordRow
                actions={
                  isDeletableLocalSession(session)
                    ? [
                        <DeleteLocalDraftButton
                          isDeleting={deletingSessionId === session.id}
                          isSubmitted
                          key="delete"
                          onDelete={() => void deleteLocalDraft(session.id).then(refreshStorageHealth)}
                        />,
                      ]
                    : undefined
                }
                detailsFallback={intl.formatMessage(messages.metersNoPlacementDetails)}
                onOpen={() => openSession(session, onOpenQueuedMeter)}
                session={session}
              />
            )}
          />
        ) : (
          <Empty
            description={
              isLoadingQueuedMeters
                ? intl.formatMessage(messages.todayLoadingRecentMeters)
                : intl.formatMessage(messages.todayNoRecentMeters)
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>

      {isLoadingStats ? (
        <Typography.Text type="secondary">{intl.formatMessage(messages.todayRefreshingLocationStats)}</Typography.Text>
      ) : null}
    </Space>
  );
}

function isLocalDraft(session: CaptureSession) {
  return session.status === CAPTURE_SESSION_STATUSES.draft || session.status === CAPTURE_SESSION_STATUSES.ready;
}

function openSession(session: CaptureSession, onOpenQueuedMeter: (sessionId: string) => void) {
  onOpenQueuedMeter(session.id);
}
